// Canonical User Activity Log (UAL) action codes from the UAL registry.
// These values are sent as the `source` request header on the mutation calls
// that perform each tracked action, and are also used to label the Action
// column on the User Activity screen. Keep these in sync with the backend
// UAL registry — they are the single source of truth for tracked actions.
export const UAL_ACTIONS = {
  ARI_BULK_EDITED: "ARI_BULK_EDITED",
  ARI_INDIVIDUAL_EDITED: "ARI_INDIVIDUAL_EDITED",
  ARI_STOP_SELL: "ARI_STOP_SELL",
  RESERVATION_VCC_REPUSHED: "RESERVATION_VCC_REPUSHED",
  RESERVATION_PMS_ID_ADDED: "RESERVATION_PMS_ID_ADDED",
  RESERVATION_PMS_ID_EDITED: "RESERVATION_PMS_ID_EDITED",
  RESERVATION_CXL_ID_ADDED: "RESERVATION_CXL_ID_ADDED",
  RESERVATION_CXL_ID_EDITED: "RESERVATION_CXL_ID_EDITED",
  // Promotions. A promo IS a derived rate — the promo screen writes a derived
  // rate plan via the metadata service — so there is one entity and one set of
  // actions. The registry emits these as PROMO_*, not DERIVED_RATE_*: keying them
  // on the latter meant no promo event ever matched, so the Action column fell
  // back to the raw code. Mirror of ual-context-promotions/ual-registry/promotions.yml.
  PROMO_CREATED: "PROMO_CREATED",
  PROMO_EDITED: "PROMO_EDITED",
  PROMO_ENABLED: "PROMO_ENABLED",
  PROMO_DISABLED: "PROMO_DISABLED",
  PROMO_DELETED: "PROMO_DELETED",
};

// Human-readable labels for each action, used by the User Activity Action column and by
// the details modal's Type row (which drops the area prefix, since its badge carries it).
// Title Case throughout — the action reads as a name on both surfaces, so "Bulk Update",
// not "Bulk update".
export const UAL_ACTION_LABELS = {
  // "Bulk Update" is the product's word for this on the ARI screens; the action key
  // stays ARI_BULK_EDITED because that is what the registry stores.
  [UAL_ACTIONS.ARI_BULK_EDITED]: "ARI — Bulk Update",
  [UAL_ACTIONS.ARI_INDIVIDUAL_EDITED]: "ARI — Individual Edited",
  [UAL_ACTIONS.ARI_STOP_SELL]: "ARI — Stop Sell",
  [UAL_ACTIONS.RESERVATION_VCC_REPUSHED]: "Reservation — Repushed VCC",
  [UAL_ACTIONS.RESERVATION_PMS_ID_ADDED]: "Reservation — PMS ID Added",
  [UAL_ACTIONS.RESERVATION_PMS_ID_EDITED]: "Reservation — PMS ID Edited",
  [UAL_ACTIONS.RESERVATION_CXL_ID_ADDED]: "Reservation — CXL ID Added",
  [UAL_ACTIONS.RESERVATION_CXL_ID_EDITED]: "Reservation — CXL Update",
  // Labelled "Promo" rather than "Derived rate": the stored entity is a derived
  // rate, but Promotions is the screen a user searching the activity log is
  // thinking of.
  [UAL_ACTIONS.PROMO_CREATED]: "Promo — Created",
  [UAL_ACTIONS.PROMO_EDITED]: "Promo — Edited",
  [UAL_ACTIONS.PROMO_ENABLED]: "Promo — Enabled",
  [UAL_ACTIONS.PROMO_DISABLED]: "Promo — Disabled",
  [UAL_ACTIONS.PROMO_DELETED]: "Promo — Deleted",
};

// Details-modal overrides for the Type row. The table's Action column wants the area up
// front so a mixed list sorts and scans by area ("Promo — Edited"); the modal already has
// the area in its badge and the design names the operation instead ("Edit Promo", Figma:
// User Activity Logs). Stripping the area prefix off the table label gave a bare "Edited",
// which reads as a fragment. Only the promo actions differ; everything else falls back to
// the stripped table label, which is already how ARI reads ("Stop Sell", "Bulk Update").
export const UAL_ACTION_DETAIL_LABELS = {
  // "Repushed VCC", not the table label's "VCC Repushed" — the badge beside it already
  // says Res Report, so the row names the operation (Figma: User Activity Logs).
  [UAL_ACTIONS.RESERVATION_VCC_REPUSHED]: "Repushed VCC",
  [UAL_ACTIONS.PROMO_CREATED]: "Create Promo",
  [UAL_ACTIONS.PROMO_EDITED]: "Edit Promo",
  [UAL_ACTIONS.PROMO_ENABLED]: "Enable Promo",
  [UAL_ACTIONS.PROMO_DISABLED]: "Disable Promo",
  [UAL_ACTIONS.PROMO_DELETED]: "Delete Promo",
};

// FE surface identifiers sent as the `X-Source` request header on UAL-tracked
// mutations. This is the SCREEN the action came from — NOT the action code. The
// backend writes it into the event's `source` field and uses it to discriminate
// by_source registry entries; the action itself is set server-side. Keep these
// in sync with the backend *Actions.Source constants (e.g. BookingActions.Source).
export const UAL_SOURCES = {
  // ARI management screens. The backend resolves each to a stored action via the
  // registry `by_source` map (e.g. table + calendar-bulk -> ARI_BULK_EDITED).
  // Mirror of AriActions.Source. NOTE: "calender" is misspelled to match the
  // backend key verbatim — do not "correct" it.
  ARI_CALENDAR_BULK: "supplier_portal_ari_management_calender_bulk",
  ARI_TABLE: "supplier_portal_ari_management_table",
  // Excel upload is a bulk edit — the backend resolves it to ARI_BULK_EDITED,
  // same as table/calendar-bulk. Mirror of AriActions.Source.EXCEL_UPLOAD.
  ARI_EXCEL_UPLOAD: "supplier_portal_ari_management_excel_upload",
  ARI_CALENDAR_INDIVIDUAL: "supplier_portal_ari_management_calender_individual",
  ARI_STOP_SELL: "supplier_portal_ari_management_stop_sell",
  // Reservation Report screen (booking actions are uniform — no by_source).
  // Mirror of BookingActions.Source.
  RESERVATION_REPORT: "supplier_portal_reservation_report",
  // Promotions screens. Mirror of PromotionActions.Source. The backend picks the
  // action from the endpoint it served, not from this header — enable, disable
  // and delete are all one PATCH .../status call, so the source only records
  // WHICH SCREEN the user acted from.
  //
  // There is no Action Center source: an Action Center recommendation prefills
  // the create form and navigates to it, so those creates come from the form
  // like any other. The SMART/CUSTOM distinction is carried by promoType.
  PROMOTIONS_CREATE_FORM: "supplier_portal_promotions_create_form",
  PROMOTIONS_LIST: "supplier_portal_promotions_list",
};

// Resolve the PMS/CXL reservation-id action code from the edited field and
// whether the field was previously empty (added) or already had a value (edited).
export const reservationIdAction = (dataKey, wasEmpty) => {
  if (dataKey === "cxlId") {
    return wasEmpty
      ? UAL_ACTIONS.RESERVATION_CXL_ID_ADDED
      : UAL_ACTIONS.RESERVATION_CXL_ID_EDITED;
  }
  return wasEmpty
    ? UAL_ACTIONS.RESERVATION_PMS_ID_ADDED
    : UAL_ACTIONS.RESERVATION_PMS_ID_EDITED;
};
